import { stateWord, UNKNOWN_STATE_WORD } from "@lafayette/domain";
import { type ReactNode, useId } from "react";
import { Link, useParams } from "react-router";
import { deepLinks } from "../../navigation/deep-links";
import { areaPath } from "../../navigation/offering";
import { useAreaHref } from "../../navigation/use-offering";
import { Amount } from "../../ui/amount";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { Icon } from "../../ui/icon";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { useTeamCapabilities } from "../access";
import { describeTeamError } from "../errors";
import { paiseText, utcText } from "../format";
import {
  activityKindLabels,
  assignmentTypeLabels,
  conversationModeLabels,
  handoffStatusLabels,
} from "../labels";
import { useCustomerSupport } from "../queries";

// The support view of one customer (CHT-768): the safe alternative to logging in as the customer. A staff member looks at the customer
// through their OWN access: who the customer is, who covers them, the open conversation, requests and payments, the trips and what
// happened last, each with a link to the screen that owns it. Nothing here signs anybody in as the customer, mints a customer session,
// shows message text or changes anything. A supervisor reads every customer; a concierge the customers assigned to them (the API decides:
// another customer looks the same as one that does not exist).

export function CustomerSupportPage() {
  const { customerId = "" } = useParams();
  const { capabilities, isLoading: rolesLoading } = useTeamCapabilities();
  const support = useCustomerSupport(customerId, capabilities.readCustomers && customerId !== "");

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.readCustomers) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include customer support views."
      />
    );
  }
  if (support.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the customer" message="Waiting for the answer." />
    );
  }
  if (support.isError) {
    return (
      <AsyncState
        kind="error"
        title="The customer could not be opened"
        message={describeTeamError(support.error).message}
        onRetry={() => void support.refetch()}
      />
    );
  }
  return <SupportBody data={support.data} busy={support.isFetching} />;
}

type SupportData = NonNullable<ReturnType<typeof useCustomerSupport>["data"]>;
type AreaHref = ReturnType<typeof useAreaHref>;

function SupportBody({ data, busy }: Readonly<{ data: SupportData; busy: boolean }>) {
  const areaHref = useAreaHref();
  // Each link opens another area, so it is a link only while that area opens for this staff member (the offering).
  // The server says whether this staff member may read the memory of THIS customer (`mayReadMemory`): that stays as a check on top of the area.
  const memoryHref = data.viewer.mayReadMemory
    ? areaHref("customers", deepLinks.customerMemory(data.customer.id))
    : null;
  const privacyHref = areaHref("privacy", deepLinks.customerPrivacy(data.customer.id));

  return (
    <section
      className="grid grid-cols-[minmax(0,1fr)] gap-6"
      aria-labelledby="page-title"
      aria-busy={busy}
    >
      <PageHeader
        breadcrumbs={<BackLink to={areaPath("customers")}>Back to customers</BackLink>}
        eyebrow="Customer support view"
        title={data.customer.displayName}
        description="You see this customer through your own staff access. Nothing here signs you in as the customer, and nothing here shows message text."
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,17rem),1fr))] gap-4">
        <CoverCard data={data} areaHref={areaHref} />
        <ConversationCard data={data} areaHref={areaHref} />
        <RequestsCard data={data} areaHref={areaHref} />
        <PaymentsCard data={data} areaHref={areaHref} />
        <TripsCard data={data} areaHref={areaHref} />
        {memoryHref === null ? null : (
          <SupportCard id="support-memory" title="Memory">
            <Muted block>What Lafayette remembers about this customer.</Muted>
            <ActionLink to={memoryHref} pinned>
              Open memory
            </ActionLink>
          </SupportCard>
        )}
      </div>

      {/* The wrapper is the container: a container query cannot restyle its own container. */}
      <div className="@container">
        <div
          className={cn(
            // Privacy keeps 364 px beside the activity only when the activity keeps at least 448 px of rows (the container, not the window,
            // says so: the sidebar takes a share of the window). Under that width the two cards stack.
            "grid gap-4",
            privacyHref === null
              ? undefined
              : "@min-[55rem]:grid-cols-[minmax(0,22.75rem)_minmax(0,1fr)]",
          )}
        >
          {privacyHref === null ? null : (
            <SupportCard id="support-privacy" title="Privacy">
              <Muted block>
                Export or deletion of this customer's data. Another person approves it.
              </Muted>
              <ActionLink to={privacyHref} pinned>
                Open privacy requests
              </ActionLink>
            </SupportCard>
          )}
          <ActivityCard data={data} areaHref={areaHref} />
        </div>
      </div>
    </section>
  );
}

type CardProps = Readonly<{ data: SupportData; areaHref: AreaHref }>;

function CoverCard({ data, areaHref }: CardProps) {
  const coverageHref = data.viewer.mayManageCoverage
    ? areaHref("coverage", deepLinks.customerCoverage(data.customer.id))
    : null;
  return (
    <SupportCard id="support-cover" title="Assigned concierge">
      {data.coverage.assignments.length === 0 ? (
        <Warning>Nobody covers this customer. Work goes to the supervisor queue.</Warning>
      ) : (
        <Rows>
          {data.coverage.assignments.map((row) => (
            <li key={row.assignmentId} className="flex flex-col">
              <strong className="text-[15px] leading-[22px] font-semibold">
                {row.staff.displayName}
              </strong>{" "}
              <Muted>
                {assignmentTypeLabels[row.type]}
                {row.endsAt === null ? "" : `, until ${utcText(row.endsAt)}`}
              </Muted>
            </li>
          ))}
        </Rows>
      )}
      {!data.coverage.hasEffectivePrimary && data.coverage.assignments.length > 0 ? (
        <Warning announce={false}>There is no primary concierge.</Warning>
      ) : null}
      {coverageHref === null ? null : <ActionLink to={coverageHref}>Edit the cover</ActionLink>}
    </SupportCard>
  );
}

function ConversationCard({ data, areaHref }: CardProps) {
  const conversationHref =
    data.conversation === null
      ? null
      : areaHref("concierge", deepLinks.conversation(data.conversation.id));
  return (
    <SupportCard id="support-conversation" title="Conversation">
      {data.conversation === null ? (
        <Muted block>No open conversation, or it is not open to your role.</Muted>
      ) : (
        <>
          <div className="flex flex-col">
            <p className="m-0 text-[15px] leading-[22px]">
              {conversationModeLabels[data.conversation.mode] ?? "Open conversation"}.
            </p>
            {data.conversation.handoff === null ? null : (
              <Muted block>
                Handoff: {handoffStatusLabels[data.conversation.handoff.status] ?? "open"}.
              </Muted>
            )}
          </div>
          {conversationHref === null ? null : (
            <ActionLink to={conversationHref} pinned>
              Open the conversation
            </ActionLink>
          )}
        </>
      )}
    </SupportCard>
  );
}

function RequestsCard({ data, areaHref }: CardProps) {
  if (data.requests === null) return null;
  return (
    <SupportCard id="support-requests" title="Open requests">
      {data.requests.open.length === 0 ? (
        <Muted block>No open request.</Muted>
      ) : (
        <Rows>
          {data.requests.open.map((request, index, all) => (
            <li key={request.id} className="flex flex-col">
              <MaybeLink href={areaHref("requests", deepLinks.request(request.id))}>
                {request.title || `Request ${index + 1} of ${all.length}`}
              </MaybeLink>{" "}
              <Muted>
                {stateWord("request", "staffShort", request.state)}, updated{" "}
                {utcText(request.updatedAt)}
              </Muted>
            </li>
          ))}
        </Rows>
      )}
    </SupportCard>
  );
}

function PaymentsCard({ data, areaHref }: CardProps) {
  if (data.payments === null) return null;
  return (
    <SupportCard id="support-payments" title="Open payments">
      {data.payments.open.length === 0 ? (
        <Muted block>No payment is waiting.</Muted>
      ) : (
        <Rows>
          {data.payments.open.map((payment) => (
            <li key={payment.id} className="flex flex-col">
              <MaybeLink href={areaHref("payments", deepLinks.payment(payment.id))}>
                <Amount className="text-lg leading-6">
                  {paiseText(payment.amountMinor, payment.currency)}
                </Amount>
              </MaybeLink>{" "}
              <Muted>
                {stateWord("payment", "staffShort", payment.status)}, since{" "}
                {utcText(payment.statusChangedAt)}
              </Muted>
            </li>
          ))}
        </Rows>
      )}
    </SupportCard>
  );
}

function TripsCard({ data, areaHref }: CardProps) {
  if (data.trips === null) return null;
  return (
    <SupportCard id="support-trips" title="Trips">
      {data.trips.length === 0 ? (
        <Muted block>No trip.</Muted>
      ) : (
        <Rows>
          {data.trips.map((trip, index, all) => (
            <li key={trip.id} className="flex flex-col">
              <MaybeLink href={areaHref("trips", deepLinks.tripItinerary(trip.id))}>
                {trip.title || `Trip ${index + 1} of ${all.length}`}
              </MaybeLink>{" "}
              <Muted>
                {stateWord("trip", "staffShort", trip.status)}
                {trip.startsOn === null
                  ? ""
                  : `, ${trip.startsOn}${trip.endsOn === null ? "" : ` to ${trip.endsOn}`}`}
              </Muted>
            </li>
          ))}
        </Rows>
      )}
    </SupportCard>
  );
}

/** Where a row of the recent activity leads: the screen that owns the thing, or nothing while its area is closed. */
function activityHref(
  entry: SupportData["recentActivity"][number],
  data: SupportData,
  areaHref: AreaHref,
): string | null {
  switch (entry.kind) {
    case "request":
      return areaHref("requests", deepLinks.request(entry.id));
    case "trip":
      return areaHref("trips", deepLinks.tripItinerary(entry.id));
    case "payment":
      return areaHref("payments", deepLinks.payment(entry.id));
    default:
      return data.conversation === null
        ? null
        : areaHref("concierge", deepLinks.conversation(data.conversation.id));
  }
}

function ActivityCard({ data, areaHref }: CardProps) {
  return (
    <SupportCard id="support-activity" title="Recent activity">
      {data.recentActivity.length === 0 ? (
        <Muted block>Nothing recent.</Muted>
      ) : (
        <ol className="@container m-0 grid list-none p-0" aria-label="Newest first">
          {data.recentActivity.map((entry) => {
            const href = activityHref(entry, data, areaHref);
            return (
              <ActivityRow
                key={`${entry.kind}-${entry.id}`}
                when={utcText(entry.at)}
                status={statusWord(entry.kind, entry.code)}
              >
                {(ids) =>
                  href === null ? (
                    <span>{activityKindLabels[entry.kind]}</span>
                  ) : (
                    <TextLink to={href} {...ids}>
                      {activityKindLabels[entry.kind]}
                    </TextLink>
                  )
                }
              </ActivityRow>
            );
          })}
        </ol>
      )}
    </SupportCard>
  );
}

/** The state word of one activity row, by the kind of the row. A code that is not known reads as the neutral word, never as the code. */
const statusWord = (kind: keyof typeof activityKindLabels, code: string): string => {
  switch (kind) {
    case "request":
      return stateWord("request", "staffShort", code);
    case "payment":
      return stateWord("payment", "staffShort", code);
    case "trip":
      return stateWord("trip", "staffShort", code);
    case "handoff":
      return handoffStatusLabels[code] ?? UNKNOWN_STATE_WORD.staffShort;
  }
};

/**
 * One line of the recent activity: the time, the kind (a link when the screen exists) and the state. A link is named by its own words,
 * then the time and the state (`aria-labelledby`), so the links of two requests or two payments do not share a name.
 */
function ActivityRow({
  when,
  status,
  children,
}: Readonly<{
  when: string;
  status: string;
  children: (ids: { id: string; "aria-labelledby": string }) => ReactNode;
}>) {
  const own = useId();
  const time = useId();
  const state = useId();
  return (
    <li className="flex min-h-10 flex-wrap items-center gap-x-4 border-b border-hairline py-1.5 last:border-b-0">
      <span id={time} className="w-full shrink-0 text-sm text-muted-foreground @md:w-[180px]">
        {when}
      </span>{" "}
      <span className="shrink-0 text-[15px] leading-[22px] @md:w-[120px]">
        {children({ id: own, "aria-labelledby": `${own} ${time} ${state}` })}
      </span>{" "}
      <span id={state} className="text-sm text-muted-foreground">
        {status}
      </span>
    </li>
  );
}

function SupportCard({
  id,
  title,
  children,
}: Readonly<{ id: string; title: string; children: ReactNode }>) {
  return (
    <section aria-labelledby={id} className="min-w-0">
      <Card className="flex h-full flex-col gap-3.5 p-5">
        <CardTitle id={id}>{title}</CardTitle>
        {children}
      </Card>
    </section>
  );
}

const Rows = ({ children }: Readonly<{ children: ReactNode }>) => (
  <ul className="m-0 grid list-none gap-3 p-0">{children}</ul>
);

function Muted({ children, block = false }: Readonly<{ children: ReactNode; block?: boolean }>) {
  return block ? (
    <p className="m-0 text-sm text-muted-foreground">{children}</p>
  ) : (
    <span className="text-sm text-muted-foreground">{children}</span>
  );
}

function Warning({
  children,
  announce = true,
}: Readonly<{ children: ReactNode; announce?: boolean }>) {
  return (
    <p
      role={announce ? "status" : undefined}
      className="m-0 flex items-start gap-2 rounded-sm border border-warning-border bg-warning-soft px-3.5 py-2.5 text-sm leading-6 text-warning"
    >
      <Icon name="alert-02" className="mt-1 size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

const textLink =
  "text-[15px] leading-[22px] text-link underline underline-offset-4 hover:no-underline";

function TextLink({
  to,
  children,
  ...rest
}: Readonly<{
  to: string;
  children: ReactNode;
  id?: string;
  "aria-labelledby"?: string;
}>) {
  return (
    <Link className={textLink} to={to} {...rest}>
      {children}
    </Link>
  );
}

/** The words as a link when the area opens (`href` is given), else as plain text. */
function MaybeLink({ href, children }: Readonly<{ href: string | null; children: ReactNode }>) {
  return href === null ? <span>{children}</span> : <TextLink to={href}>{children}</TextLink>;
}

/** The button that leaves a card for the screen that owns the thing. `pinned` puts it at the foot of the card, in line with its neighbours. */
function ActionLink({
  to,
  pinned = false,
  children,
}: Readonly<{ to: string; pinned?: boolean; children: ReactNode }>) {
  return (
    <div className={pinned ? "mt-auto" : undefined}>
      <Button asChild variant="outline" size="sm">
        <Link to={to}>{children}</Link>
      </Button>
    </div>
  );
}
